import type { ApiMessage, ApiSponsoredMessage } from '../../api/types';

export type MessageKey = `msg${string}-${number}`;

export function getMessageKey(message: ApiMessage | ApiSponsoredMessage): MessageKey {
  const {
    chatId,
  } = message;

  // A sponsored message is the one that has no `id` — `randomId` is all it is identified by. Telling
  // the two apart by the presence of `randomId` used to be enough, but the backend echoes the send
  // `randomId` back inside every message it accepts, so the user's own messages carry the field too
  // and hashed to `Number('mt8dgfbx8l1xkp-2147483654')` — `NaN`, the very same key for all of them.
  // Everything keyed by this then shared one entry: a reaction arriving on one message replayed its
  // effect on every message the user had sent to that chat, and the guard that drops a stale
  // `sendReaction` response could not tell two messages apart either.
  if (!('id' in message)) {
    return buildMessageKey(chatId, Number(message.randomId));
  }
  return buildMessageKey(chatId, message.previousLocalId || message.id);
}

export function getMessageServerKey(message: ApiMessage): MessageKey | undefined {
  if (isLocalMessageId(message.id)) {
    return undefined;
  }
  const { chatId, id } = message;
  return buildMessageKey(chatId, id);
}

export function buildMessageKey(chatId: string, msgId: number): MessageKey {
  return `msg${chatId}-${msgId}`;
}

export function parseMessageKey(key: MessageKey) {
  const match = key.match(/^msg(-?\d+)-(\d+)/)!;

  return { chatId: match[1], messageId: Number(match[2]) };
}

// A message being sent has no server id yet, so it is given a local one. Local ids have to sort after
// every server id — the message list orders by id, and the thing you just sent is the newest thing in
// the chat. The previous scheme put them in the gap between the last known server id and the next
// integer (`lastMessageId + n/1e6`), which only held until an earlier message of the same burst
// reconciled: its real id (`lastMessageId + 1`) then leapfrogged every sibling still in flight, so
// sending several messages quickly visibly re-shuffled the bubbles until the last echo arrived.
// Allocating above the server id space keeps the order stable no matter when the echoes land, and
// keeps every id list monotonic — which the sorted-array helpers around them assume.
export const LOCAL_MESSAGE_MIN_ID = 0x80000000;

// Local ids are handed out from two places that never share memory: the message builder, which runs
// in the API worker, and the typing-draft path on the main thread. Each holds its own counter, so
// they allocate from interleaved halves of the range — otherwise both would eventually name a
// different message with the same id. (`incrementLocalMessagesCounter` used to nod at this problem,
// but it resolves to nothing in this fork, so the counters were never actually in step.)
let workerLocalMessageCounter = 0;
let uiLocalMessageCounter = 0;

export function getNextLocalMessageId() {
  workerLocalMessageCounter += 2;
  return LOCAL_MESSAGE_MIN_ID + workerLocalMessageCounter;
}

export function getNextUiLocalMessageId() {
  uiLocalMessageCounter += 2;
  return LOCAL_MESSAGE_MIN_ID + 1 + uiLocalMessageCounter;
}

export function isLocalMessageId(id: number) {
  // Fractional ids are still produced for the synthetic channel-join message, and remain in state
  // cached by an older build, so keep recognizing them.
  return id >= LOCAL_MESSAGE_MIN_ID || !Number.isInteger(id);
}
